/**
 * 接口封装：同源 /v1（由 Worker 转发到 kp-api，Worker 用会话 Cookie 补上 Authorization，页面拿不到令牌）。
 * - 每个请求都带 X-KP-Web: 1（防 CSRF，见 docs/网页版.md）；
 * - JSON 键名 camelCase ↔ snake_case，透传字段不转换（json.ts）；
 * - 401 时刷新一次令牌再重试：同一页面的并发请求共用一次刷新，多个标签页用 Web Locks 串行。
 */
import { fromApi, toApi } from "./json";
import type { Asset, UploadTicket } from "./models";

export const CSRF_HEADER = "X-KP-Web";

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }
}

export type Query = Record<string, string | number | boolean | null | undefined>;

export interface RequestOptions {
  query?: Query;
  /** 请求体（会做 camelCase → snake_case 转换，除非 raw） */
  body?: unknown;
  /** 不做键名转换（请求体和响应都原样） */
  raw?: boolean;
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

// ---------- 会话失效回调 ----------

let unauthorizedHandler: (() => void) | null = null;

/** 刷新令牌也失败（登录过期）时调用，session.ts 用它切回登录页 */
export function onUnauthorized(handler: (() => void) | null): void {
  unauthorizedHandler = handler;
}

function sessionExpired(): ApiError {
  unauthorizedHandler?.();
  return new ApiError(401, "unauthorized", "登录已过期，请重新登录");
}

// ---------- 登录状态与刷新 ----------

export interface AuthStatus {
  loggedIn: boolean;
  /** access token 过期时间（毫秒） */
  accessExpiresAt: number | null;
  /** 本地开发：可以用演示账号密码登录 */
  devLogin: boolean;
}

export async function fetchAuthStatus(): Promise<AuthStatus> {
  let res: Response;
  try {
    res = await fetch("/auth/status", { headers: { Accept: "application/json" }, cache: "no-store" });
  } catch {
    throw new ApiError(0, "network", "网络连接失败，请检查网络");
  }
  if (!res.ok) throw await errorFrom(res);
  return fromApi<AuthStatus>(await res.json());
}

/** refreshed：换了新令牌；skipped：别的标签页刚刷新过，直接重试；failed：登录已过期 */
export type RefreshOutcome = "refreshed" | "skipped" | "failed";

/** 离过期还有这么久就认为别的标签页已经刷新过 */
const FRESH_MS = 30_000;
let refreshing: Promise<RefreshOutcome> | null = null;

/**
 * 刷新令牌。同一页面的并发调用共用一次；多个标签页用 Web Locks（kp-refresh）串行，
 * 拿到锁后先查 /auth/status：access token 还有 30 秒以上才过期，说明别的标签页已经刷新过，不再刷新
 * （旧的 refresh token 再次使用会让后端吊销整条令牌链）。force=true 跳过这个检查。
 */
export function refreshSession(force = false): Promise<RefreshOutcome> {
  if (!refreshing) {
    refreshing = withRefreshLock(() => doRefresh(force)).finally(() => {
      refreshing = null;
    });
  }
  return refreshing;
}

function withRefreshLock<T>(fn: () => Promise<T>): Promise<T> {
  const locks = typeof navigator !== "undefined" ? navigator.locks : undefined;
  if (locks?.request) return locks.request("kp-refresh", fn) as Promise<T>;
  return fn();
}

async function doRefresh(force: boolean): Promise<RefreshOutcome> {
  if (!force) {
    const st = await fetchAuthStatus();
    if (!st.loggedIn) return "failed";
    if (st.accessExpiresAt != null && st.accessExpiresAt - Date.now() > FRESH_MS) return "skipped";
  }
  let res: Response;
  try {
    res = await fetch("/auth/refresh", { method: "POST", headers: { [CSRF_HEADER]: "1", Accept: "application/json" } });
  } catch {
    throw new ApiError(0, "network", "网络连接失败，请检查网络");
  }
  if (res.status === 401) return "failed";
  // 503：登录服务暂时不可用，不当作登录过期
  if (!res.ok) throw await errorFrom(res);
  return "refreshed";
}

// ---------- 请求 ----------

/** "family" → "/v1/family"；以 / 开头的路径原样使用（例如 "/mcp"） */
export function apiPath(path: string, query?: Query): string {
  let p = path.startsWith("/") ? path : `/v1/${path}`;
  if (query) {
    const q = new URLSearchParams();
    for (const [k, v] of Object.entries(query)) if (v !== undefined && v !== null) q.set(k, String(v));
    const s = q.toString();
    if (s) p += (p.includes("?") ? "&" : "?") + s;
  }
  return p;
}

async function errorFrom(res: Response): Promise<ApiError> {
  try {
    const body = (await res.json()) as { error?: { code?: string; message?: string } };
    if (body?.error?.code) return new ApiError(res.status, body.error.code, body.error.message ?? `请求失败（${res.status}）`);
  } catch {
    // 不是 JSON
  }
  return new ApiError(res.status, `http_${res.status}`, `请求失败（${res.status}）`);
}

async function send(method: string, path: string, opts: RequestOptions): Promise<Response> {
  const headers: Record<string, string> = { [CSRF_HEADER]: "1", Accept: "application/json", ...opts.headers };
  const init: RequestInit = { method, headers, signal: opts.signal };
  if (opts.body !== undefined) {
    headers["Content-Type"] = "application/json";
    init.body = JSON.stringify(opts.raw ? opts.body : toApi(opts.body));
  }
  try {
    return await fetch(apiPath(path, opts.query), init);
  } catch (e) {
    if ((e as Error)?.name === "AbortError") throw e;
    throw new ApiError(0, "network", "网络连接失败，请检查网络");
  }
}

export async function request<T = unknown>(method: string, path: string, opts: RequestOptions = {}): Promise<T> {
  let res = await send(method, path, opts);
  if (res.status === 401) {
    const first = await refreshSession();
    if (first === "failed") throw sessionExpired();
    res = await send(method, path, opts);
    // 没刷新就重试仍然 401（令牌被吊销等）：强制刷新一次
    if (res.status === 401 && first === "skipped") {
      if ((await refreshSession(true)) === "failed") throw sessionExpired();
      res = await send(method, path, opts);
    }
    if (res.status === 401) throw sessionExpired();
  }
  if (!res.ok) throw await errorFrom(res);
  const text = await res.text();
  if (!text) return undefined as T;
  let json: unknown;
  try {
    json = JSON.parse(text);
  } catch {
    throw new ApiError(res.status, "decoding", `数据格式错误：${path}`);
  }
  return (opts.raw ? json : fromApi(json)) as T;
}

export const api = {
  get: <T = unknown>(path: string, query?: Query, opts?: Omit<RequestOptions, "query" | "body">) => request<T>("GET", path, { ...opts, query }),
  post: <T = unknown>(path: string, body?: unknown, opts?: Omit<RequestOptions, "body">) => request<T>("POST", path, { ...opts, body: body ?? {} }),
  patch: <T = unknown>(path: string, body: unknown, opts?: Omit<RequestOptions, "body">) => request<T>("PATCH", path, { ...opts, body }),
  put: <T = unknown>(path: string, body: unknown, opts?: Omit<RequestOptions, "body">) => request<T>("PUT", path, { ...opts, body }),
  del: <T = { ok: boolean }>(path: string, opts?: Omit<RequestOptions, "body">) => request<T>("DELETE", path, opts),
};

// ---------- 上传与下载 ----------

/** 后端签发的、需要改走 Worker 的绝对地址前缀（本地存储驱动的 /v1/dev-storage/ 地址） */
const API_ORIGINS = ["https://kp-api.ffjd.org", "http://localhost:8792", "http://127.0.0.1:8792", "https://kp-api.internal"];

/**
 * 签名 URL 改成页面可用的地址：相对路径按页面同源解析；指向 kp-api 的绝对地址改成同源路径（经 Worker 转发）；
 * 其它（OSS）原样返回。
 */
export function resolveSignedUrl(url: string, pageOrigin = typeof location !== "undefined" ? location.origin : "http://localhost"): string {
  for (const o of API_ORIGINS) {
    if (url === o || url.startsWith(`${o}/`)) return url.slice(o.length) || "/";
  }
  if (/^[a-z][a-z0-9+.-]*:/i.test(url)) return url;
  const u = new URL(url, pageOrigin);
  return u.pathname + u.search;
}

const MIME_BY_EXT: Record<string, string> = {
  pdf: "application/pdf",
  mp4: "video/mp4",
  mov: "video/quicktime",
  m4v: "video/x-m4v",
  mp3: "audio/mpeg",
  m4a: "audio/mp4",
  wav: "audio/wav",
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  gif: "image/gif",
  webp: "image/webp",
  zip: "application/zip",
  json: "application/json",
};

export function guessMime(file: { name: string; type?: string }): string {
  if (file.type) return file.type;
  const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
  return MIME_BY_EXT[ext] ?? "application/octet-stream";
}

/** 文件的 SHA-256（十六进制）。crypto.subtle 不支持流式，整个文件读进内存 */
export async function sha256Hex(blob: Blob): Promise<string> {
  const buf = await blob.arrayBuffer();
  const digest = await crypto.subtle.digest("SHA-256", buf);
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, "0")).join("");
}

export interface UploadOptions {
  /** 上传进度 0~1 */
  onProgress?: (fraction: number) => void;
  /** 当前阶段的说明（"正在准备文件…" 等） */
  onStage?: (text: string) => void;
  contentId?: string;
  signal?: AbortSignal;
}

function putWithProgress(url: string, method: string, headers: Record<string, string>, file: Blob, opts: UploadOptions): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, url);
    for (const [k, v] of Object.entries(headers)) xhr.setRequestHeader(k, v);
    // 同源地址（经 Worker 转发）也带上防 CSRF 头
    if (url.startsWith("/")) xhr.setRequestHeader(CSRF_HEADER, "1");
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) opts.onProgress?.(e.loaded / e.total);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        opts.onProgress?.(1);
        resolve();
      } else reject(new ApiError(xhr.status, "upload_failed", `上传失败（${xhr.status}）`));
    };
    xhr.onerror = () => reject(new ApiError(0, "network", "上传失败，请检查网络"));
    xhr.onabort = () => reject(new DOMException("上传已取消", "AbortError"));
    opts.signal?.addEventListener("abort", () => xhr.abort(), { once: true });
    xhr.send(file);
  });
}

/**
 * 上传文件：算 SHA-256 → POST uploads → PUT 到签名 URL（带进度）→ POST assets/{id}/complete。
 * 返回 ready 状态的 Asset（调用方用 didWrite("assets", asset) 放进本地数据）。
 */
export async function upload(file: File, opts: UploadOptions = {}): Promise<Asset> {
  opts.onStage?.("正在准备文件…");
  const sha256 = await sha256Hex(file);
  opts.onStage?.(`正在上传 ${file.name}…`);
  // 响应里的 upload.headers 是 HTTP 头，不能做键名转换，所以用 raw 再单独转换 asset
  const raw = await api.post<{ asset: unknown; upload: UploadTicket["upload"] }>(
    "uploads",
    { file_name: file.name, mime: guessMime(file), size: file.size, sha256, ...(opts.contentId ? { content_id: opts.contentId } : {}) },
    { raw: true, signal: opts.signal },
  );
  const target = resolveSignedUrl(raw.upload.url);
  await putWithProgress(target, raw.upload.method || "PUT", raw.upload.headers ?? {}, file, opts);
  opts.onStage?.("正在确认…");
  const asset = fromApi<Asset>(raw.asset);
  return api.post<Asset>(`assets/${asset.id}/complete`, undefined, { signal: opts.signal });
}

const urlCache = new Map<string, { url: string; expiresAt: number }>();

/** 附件的下载地址（签名 URL，缓存到过期前 1 分钟）。builtin: 资源网页版没有内置包，返回 null */
export async function assetUrl(assetId: string): Promise<string | null> {
  if (assetId.startsWith("builtin:")) return null;
  const hit = urlCache.get(assetId);
  if (hit && hit.expiresAt - Date.now() > 60_000) return hit.url;
  const r = await api.get<{ url: string; expiresAt: number }>(`assets/${assetId}/url`);
  const url = resolveSignedUrl(r.url);
  urlCache.set(assetId, { url, expiresAt: r.expiresAt });
  return url;
}
